import { Tag } from 'antd';
import { UserContext } from 'contexts/ConfigContext';
import { PricingAnalyticsDataContext } from 'contexts/PricingAnalyticsContext';
import { FC, useContext } from 'react';
import { getAccessIcon, getConfig } from 'utils/helperMethods';
import AnalyticsActions from './AnalyticsActions';
import FilterRibbon from '../FilterRibbon/FilterRibbon';
import KPITiles from './KpiTiles/KpiTiles';
import AnalyticsChartContainer from './AnalyticsChartContainer';
import { KPITileItem, LenseAttribute, LenseConfig } from 'types/pricingSimulator';
import { pricingAnalyticsUrls } from 'config/urls';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import _ from 'lodash';
import { ConfigKeys } from 'utils/Enums';

const ScenarioAnalyticsContainer: FC = () => {
    const { appConfig } = useContext(UserContext);
    const pricingSimulatorConfig = getConfig('pricingSimulator', appConfig);
    const commonConfig = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);
    const { decimalForNonPercentage, decimalForPercentage } = commonConfig;

    const {
        characterLimit: { kpiTileCharLimit },
        enablePrivatePublicToggle,
    } = pricingSimulatorConfig;

    const projectStatuses = pricingSimulatorConfig.projectStatuses;
    const {
        selectedReportInfo,
        lenseConfigLoading,
        globalKpiLoading,
        updateReportProperty,
        allCheckedAttribute,
        allSelectedValueForAllAttribute,
        applyFilterObject,
        resetFilterObject,
        showIsEditedRecords,
        setAllCheckedAttribute,
        setAllSelectedValueForAllAttribute,
        setApplyFilterObject,
        showPercentageChange,
        loggedInUser,
        isUpdate,
        saveFilterConfig,
    } = useContext(PricingAnalyticsDataContext);

    const commonTileLoading = lenseConfigLoading || globalKpiLoading;

    // DELETE global kpi tile api call
    const { mutate: deleteGlobalKpiMutation, loading: deleteGlobalKpiMutationLoading } = useCommonMutationHandler({
        url: pricingAnalyticsUrls.deleteGlobalKPIUrl,
        method: 'delete',
    });

    const deleteGlobalKpiHandler = (kpiId: number) => {
        const newBaseKpis = _.cloneDeep(selectedReportInfo?.baseKPIs);
        newBaseKpis?.kpiConfig?.forEach((l: LenseConfig) => {
            l.attributes.forEach((a: LenseAttribute) => {
                if (a.attributeId === kpiId) {
                    a.isSelected = false;
                }
            });
        });

        const newKpiTiles = newBaseKpis?.kpiTiles?.filter((k: KPITileItem) => k.id !== kpiId) || [];

        // update lense isAll selected property if all attributes are selected
        newBaseKpis?.kpiConfig?.forEach((item: LenseConfig) => {
            item.isAllSelected = item.attributes.every((i: any) => i.isSelected);
            item.isSelected = item.attributes.some((i: any) => i.isSelected);
        });

        if (selectedReportInfo) {
            updateReportProperty('baseKPIs', {
                ...selectedReportInfo.baseKPIs,
                kpiTiles: newKpiTiles,
                kpiConfig: newBaseKpis?.kpiConfig,
            });
        }

        if (isUpdate) {
            // only for owner allow delete api call
            const payload = {
                report_id: selectedReportInfo?.reportId,
                kpi_id: kpiId,
                user_name: loggedInUser,
            };

            deleteGlobalKpiMutation({ ...payload });
        }
    };

    return (
        <div>
            {/* 2. Scenario Name, KPI's select control, Report actions   */}
            <div className="flex gap-2 items-center justify-between mt-3">
                <div className="flex gap-4 items-center">
                    <div className=" font-lg">{selectedReportInfo?.baseScenario?.scenarioName}</div>
                    <div className="flex items-center justify-between gap-2">
                        <span>
                            {enablePrivatePublicToggle &&
                                getAccessIcon(selectedReportInfo?.baseScenario.isPrivate || false)}
                        </span>
                        <Tag
                            style={{ borderRadius: 4 }}
                            color={
                                projectStatuses[selectedReportInfo?.baseScenario?.status ?? 'DRAFT']?.colorType ||
                                'default'
                            }
                        >
                            {projectStatuses[selectedReportInfo?.baseScenario?.status ?? 'DRAFT']?.label}
                        </Tag>
                    </div>
                </div>
                <AnalyticsActions />
            </div>
            {/*3.  Filter Ribbon  */}
            <div className="mt-3">
                <FilterRibbon
                    allCheckedAttribute={allCheckedAttribute}
                    allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                    applyFilterObject={applyFilterObject}
                    resetFilterObject={resetFilterObject}
                    showIsEditedRecords={showIsEditedRecords}
                    onSubmitHandler={(obj: any) => {
                        setAllCheckedAttribute(obj.allCheckedAttribute);
                        setAllSelectedValueForAllAttribute(obj.allSelectedValueForAllAttribute);
                        setApplyFilterObject(obj.applyFilterObject);
                        saveFilterConfig(obj);
                    }}
                />
            </div>

            {/* 4. Kpi tiles  */}
            <KPITiles
                tiles={selectedReportInfo?.baseKPIs?.kpiTiles || []}
                loading={commonTileLoading}
                onTileRemove={(kpiId: number) => {
                    deleteGlobalKpiHandler(kpiId);
                }}
                showKpiPercentage={showPercentageChange}
                kpiTileCharLimit={kpiTileCharLimit}
                decimalForNonPercentage={decimalForNonPercentage}
                decimalForPercentage={decimalForPercentage}
            />

            {/* 5.Analytics view Container  */}
            <AnalyticsChartContainer />
        </div>
    );
};

export default ScenarioAnalyticsContainer;
